import { useMemo } from 'react'
import { useAuth } from './useAuth'
import type { UserRole, Permission } from '@/constants'

/**
 * 認可フック。`/api/auth/me` で取得した `session.role`（enum key）と
 * `session.permissions`（後端 DB 由来の権限コード配列）を直接参照する。
 *
 * かつて存在した `ROLE_DISPLAY_TO_KEY` 逆引きは、後端が enum key を返すように
 * なったため不要。表示用は `session.roleDisplay` を別途参照する。
 *
 * 戻り値は session が変わらない限り同一参照を返す（useMemo で安定化）。
 * 消費側が useEffect / useMemo の依存配列にメソッドを置いても無限ループしない。
 */
export function usePermission() {
  const { session } = useAuth()
  const currentRole = (session?.role ?? null) as UserRole | null
  const permissions = session?.permissions

  return useMemo(
    () => ({
      role: currentRole,

      hasRole(role: UserRole | string): boolean {
        return currentRole != null && currentRole === role
      },

      hasAnyRole(...roles: (UserRole | string)[]): boolean {
        if (currentRole == null) return false
        return roles.some((r) => r === currentRole)
      },

      hasPermission(permission: Permission): boolean {
        return permissions?.includes(permission) ?? false
      },

      hasAnyPermission(...perms: Permission[]): boolean {
        if (!permissions || permissions.length === 0) return false
        return perms.some((p) => permissions.includes(p))
      },

      isAdmin: currentRole === 'ADMIN' || currentRole === 'SYSTEM_ADMIN',
      isSystemAdmin: currentRole === 'SYSTEM_ADMIN',
    }),
    [currentRole, permissions],
  )
}
